// The route tree (react-router-dom BrowserRouter - stack-profile.md is silent on a
// specific router/state library for apps/web, docs/architecture/stack-profile.md:14-16;
// react-router-dom is the minimal, standard choice for a Vite SPA and is the router the
// kit's own SPA-history-fallback check already expects, scripts/checks/80-spa-history-
// fallback.sh:26-29). Client state: React context + hooks (SessionProvider,
// WizardProvider) - no additional state library justified for Slice 2's scope.
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SessionProvider, RequireSession } from "./state/session.js";
import { WizardProvider, RequireWizardApp } from "./state/wizard.js";
import { AppShell } from "./components/AppShell.js";
import { SignIn } from "./screens/SignIn.js";
import { SignUp } from "./screens/SignUp.js";
import { Portfolio } from "./screens/Portfolio.js";
import { Connections } from "./screens/Connections.js";
import { AppDetail, AppDetailIndexRedirect } from "./screens/AppDetail.js";
import { AppExplained } from "./screens/AppExplained.js";
import { AskCrew } from "./screens/AskCrew.js";
import { CertifyDashboard } from "./screens/certify/CertifyDashboard.js";
import { DataRoom } from "./screens/certify/DataRoom.js";
import { EvidenceDetail } from "./screens/certify/EvidenceDetail.js";
import { TrustPageManagement } from "./screens/certify/TrustPageManagement.js";
import { CertifyEntry } from "./screens/certify/CertifyEntry.js";
import { TypePicker } from "./screens/onboarding/TypePicker.js";
import { IntentStep } from "./screens/onboarding/IntentStep.js";
import { GitHostInstall } from "./screens/onboarding/GitHostInstall.js";
import { CloudConnect } from "./screens/onboarding/CloudConnect.js";
import { RouteCard } from "./screens/onboarding/RouteCard.js";
import { SdkInstall } from "./screens/onboarding/SdkInstall.js";
import { TalkToGlim } from "./screens/validate/TalkToGlim.js";
import { VerdictScreen } from "./screens/validate/VerdictScreen.js";
import { BrandDirectionPicker } from "./screens/build/BrandDirectionPicker.js";
import { BrandMockupReview } from "./screens/build/BrandMockupReview.js";
import { MvpBuild } from "./screens/build/MvpBuild.js";
import { WorkflowValidation } from "./screens/build/WorkflowValidation.js";
import { AppBuildTab } from "./screens/build/AppBuildTab.js";
import { MoneyTab } from "./screens/money/MoneyTab.js";
import { PulseTab } from "./screens/pulse/PulseTab.js";
import { FeedbackTab } from "./screens/feedback/FeedbackTab.js";
import { ProductionBuild } from "./screens/build/ProductionBuild.js";

export const App = (): React.JSX.Element => (
  <BrowserRouter>
    <SessionProvider>
      <WizardProvider>
        <Routes>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />

          <Route
            path="/apps"
            element={
              <RequireSession>
                <AppShell title="Your apps">
                  <Portfolio />
                </AppShell>
              </RequireSession>
            }
          />
          <Route
            path="/connections"
            element={
              <RequireSession>
                <AppShell title="Connections & vault">
                  <Connections />
                </AppShell>
              </RequireSession>
            }
          />
          {/* The global "Trust & Certify" nav destination (docs/design/
              slice4-certify-ux.md §2.2) - a small app picker/redirect, wrapped in the
              shared AppShell like Apps/Connections above. */}
          <Route
            path="/certify"
            element={
              <RequireSession>
                <AppShell title="Trust & Certify">
                  <CertifyEntry />
                </AppShell>
              </RequireSession>
            }
          />

          <Route
            path="/apps/:appId"
            element={
              <RequireSession>
                <AppDetail />
              </RequireSession>
            }
          >
            <Route index element={<AppDetailIndexRedirect />} />
            <Route path="explained" element={<AppExplained />} />
            <Route path="ask" element={<AskCrew />} />
            <Route path="certify" element={<CertifyDashboard />} />
            <Route path="certify/data-room" element={<DataRoom />} />
            <Route path="certify/data-room/:documentId" element={<EvidenceDetail />} />
            <Route path="certify/trust-page" element={<TrustPageManagement />} />
            {/* Build tab (Flow 5/6 tail; docs/phase-0/slice5-ux-flows.md §2.4, §8.7) -
                the fourth AppDetail sub-nav tab, alongside Explained/Ask/Certify. Nested
                (unlike the two standalone routes below) because `AppBuildTab` reads
                `useOutletContext<AppDetailContext>()` (AppBuildTab.tsx:15) - it requires
                the ambient `AppShell` + `Outlet` this parent route already provides. */}
            <Route path="build" element={<AppBuildTab />} />
            {/* Slice 6 - Money / Pulse / Feedback (ADR-0023): AppDetail sub-nav tabs, nested
                because each reads useOutletContext<AppDetailContext>() like AppBuildTab. */}
            <Route path="money" element={<MoneyTab />} />
            <Route path="pulse" element={<PulseTab />} />
            <Route path="feedback" element={<FeedbackTab />} />
          </Route>

          {/* Workflow-validation re-run and Production Build (Flow 5 tail/Flow 6;
              docs/phase-0/slice5-ux-flows.md §7-9) - standalone, NOT nested under
              `/apps/:appId`: both screens render their own `FocusedFlowShell` chrome
              (WorkflowValidation.tsx, ProductionBuild.tsx), the same minimal flow shell
              every `/apps/new/*` wizard screen below uses, never the full `AppShell` +
              sub-nav `AppDetail` wraps its children in - nesting here would double that
              chrome. Neither uses `useOutletContext`; both read `appId` from the route
              param directly (`useParams<{ appId: string }>()`), so standalone is
              sufficient. `RequireWizardApp` does not apply (no in-flight wizard draft
              here, a real existing app) - `RequireSession` only, matching every other
              real-app route. */}
          <Route
            path="/apps/:appId/build/workflow"
            element={
              <RequireSession>
                <WorkflowValidation />
              </RequireSession>
            }
          />
          <Route
            path="/apps/:appId/build/production"
            element={
              <RequireSession>
                <ProductionBuild />
              </RequireSession>
            }
          />

          <Route
            path="/apps/new"
            element={
              <RequireSession>
                <TypePicker />
              </RequireSession>
            }
          />
          <Route
            path="/apps/new/intent"
            element={
              <RequireSession>
                <IntentStep />
              </RequireSession>
            }
          />
          <Route
            path="/apps/new/connect"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <GitHostInstall />
                </RequireWizardApp>
              </RequireSession>
            }
          />
          <Route
            path="/apps/new/cloud"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <CloudConnect />
                </RequireWizardApp>
              </RequireSession>
            }
          />
          <Route
            path="/apps/new/route"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <RouteCard />
                </RequireWizardApp>
              </RequireSession>
            }
          />
          <Route
            path="/apps/new/sdk"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <SdkInstall />
                </RequireWizardApp>
              </RequireSession>
            }
          />

          {/* Flow 1 - Talk-to-Glim + the verdict pack (docs/phase-0/slice5-ux-flows.md
              §4). Same `RequireSession` + `RequireWizardApp` wrapping as every sibling
              `/apps/new/*` wizard route above - both screens read `draft.appId` from
              `useWizard()` (TalkToGlim.tsx:64, VerdictScreen.tsx:39), which
              `RequireWizardApp` guarantees is set before either ever renders. */}
          <Route
            path="/apps/new/glim"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <TalkToGlim />
                </RequireWizardApp>
              </RequireSession>
            }
          />
          <Route
            path="/apps/new/glim/verdict"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <VerdictScreen />
                </RequireWizardApp>
              </RequireSession>
            }
          />

          {/* Flow 3 - brand direction picker + mockup review, the two default-deny
              approval gates (docs/phase-0/slice5-ux-flows.md §6). */}
          <Route
            path="/apps/new/brand"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <BrandDirectionPicker />
                </RequireWizardApp>
              </RequireSession>
            }
          />
          <Route
            path="/apps/new/brand/mockups"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <BrandMockupReview />
                </RequireWizardApp>
              </RequireSession>
            }
          />

          {/* Flow 5 - MVP Build (docs/phase-0/slice5-ux-flows.md §8). */}
          <Route
            path="/apps/new/build"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <MvpBuild />
                </RequireWizardApp>
              </RequireSession>
            }
          />
          {/* Flow 4 - the guided workflow-validation walkthrough, first run
              (docs/phase-0/slice5-ux-flows.md §7). `WorkflowValidation` mounts here
              wizard-scoped (`draft.appId`) and again, re-run, at the standalone
              `/apps/:appId/build/workflow` route above (route-param scoped) - one
              component, two mount points, per its own header comment
              (WorkflowValidation.tsx:1-7). */}
          <Route
            path="/apps/new/build/workflow"
            element={
              <RequireSession>
                <RequireWizardApp>
                  <WorkflowValidation />
                </RequireWizardApp>
              </RequireSession>
            }
          />

          <Route path="/" element={<Navigate to="/apps" replace />} />
          <Route path="*" element={<Navigate to="/apps" replace />} />
        </Routes>
      </WizardProvider>
    </SessionProvider>
  </BrowserRouter>
);
